import React, {createContext, useContext, useEffect, useState} from 'react';
import {FormikProvider, useFormik} from 'formik';
import {checkEmailIsValid} from '../../../../common/utils/form/checkEmailIsValid';
import {editCustomer, getCustomerDetails} from '../../../../common/actions/customers';
import {customerStatus, customerTypes, genderOptions} from '../../../../common/constants/options';

const EditCustomerContext = createContext({});

export const useEditCustomerContext = () => useContext(EditCustomerContext);

export const EditCustomerContextProvider = ({customerID, shumaID, onSuccess, onFail, children}) => {
  const [loading, setLoading] = useState(false);
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      idNumber: '',
      address: '',
      gender: {value: null},
      customerType: {value: null},
      status: {value: null},
    },
    validate: values => {
      let errors = {};

      if (!values.customerType.value) {
        errors.customerType = 'Required';
      }

      if (!values.status.value) {
        errors.status = 'Required';
      }

      switch (values.customerType.value) {
        case 1:
        case 2:
        case 3:
          ['firstName', 'idNumber'].forEach(function (item) {
            if (!values[item]) errors[item] = 'Required';
          });
          break;
        case 4:
          ['firstName', 'lastName'].forEach(function (item) {
            if (!values[item]) errors[item] = 'Required';
          });
          break;
        case 5:
        default:
          ['firstName', 'lastName', 'idNumber'].forEach(function (item) {
            if (!values[item]) errors[item] = 'Required';
          });
          break;
      }

      if (values.email && !checkEmailIsValid(values.email)) {
        errors['email'] = 'Invalid email address';
      }

      return errors;
    },
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const updatedCustomer = await editCustomer(
          customerID,
          shumaID,
          values.firstName,
          values.lastName,
          values.phone,
          values.email,
          values.customerType.value,
          values.idNumber,
          values.gender.value,
          values.address,
          values.status.value,
        );

        onSuccess(updatedCustomer);
      } catch (e) {
        onFail();
      } finally {
        setLoading(false);
      }
    }
  });

  useEffect(() => {
    setLoading(true);
    updateCustomerDetails()
      .finally(() => setLoading(false));
  }, []);

  const updateCustomerDetails = async () => {
    try {
      const customer = await getCustomerDetails(customerID);
      const [gender] = genderOptions.filter(opt => opt.value === customer.genderID);
      const [customerType] = customerTypes.filter(opt => opt.value === customer.customerType);
      const [status] = customerStatus.filter(opt => opt.value === customer.customerStatusID);
      await formik.setValues({
        ...formik.values,
        firstName: customer.firstName,
        lastName: customer.lastName,
        idNumber: customer.idNumber,
        phone: customer.phone,
        email: customer.email,
        address: customer.address,
        gender: gender || {value: null},
        customerType: customerType || {value: null},
        status: status || {value: null},
      });
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <EditCustomerContext.Provider value={{
      loading,
      isValid: formik.isValid,
      submitForm: formik.submitForm,
    }}>
      <FormikProvider value={formik}>
        {children}
      </FormikProvider>
    </EditCustomerContext.Provider>
  );
};